import { useEffect, useState } from "react";
import { bpas as taxonomyBpas } from "@/api/bpas.service";
import { SearchModal } from "../SearchModal/SearchModal";
import styles from "./TaxonomyBpaTierSelector.module.css";

type BpaNode = { id: string; name: string; parentId: string | null; children: BpaNode[] };

type TaxonomyBpaTierSelectorProps = {
  value: string | null;
  onChange: (taxonomyBpaId: string | null) => void;
  disabled?: boolean;
  variant?: "search" | "dropdown";
};

function ChevronIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path
        d="M2 4l3 3 3-3"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function buildTree(flat: Array<{ id: string; name: string; parentId?: string | null }>): BpaNode[] {
  const map = new Map<string, BpaNode>();
  const roots: BpaNode[] = [];
  for (const item of flat) {
    map.set(item.id, {
      id: item.id,
      name: item.name,
      parentId: item.parentId ?? null,
      children: [],
    });
  }
  for (const node of map.values()) {
    if (node.parentId && map.has(node.parentId)) {
      map.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function flattenVisible(
  nodes: BpaNode[],
  expanded: Set<string>,
  filter: string,
  depth: number,
): Array<{ node: BpaNode; depth: number }> {
  const result: Array<{ node: BpaNode; depth: number }> = [];
  for (const node of nodes) {
    const matchesSelf = !filter || node.name.toLowerCase().includes(filter);
    const matchesChild = !filter || hasMatchingDescendant(node.children, filter);
    if (!matchesSelf && !matchesChild) continue;
    result.push({ node, depth });
    if (node.children.length > 0 && (expanded.has(node.id) || filter)) {
      result.push(...flattenVisible(node.children, expanded, filter, depth + 1));
    }
  }
  return result;
}

function hasMatchingDescendant(nodes: BpaNode[], filter: string): boolean {
  for (const node of nodes) {
    if (node.name.toLowerCase().includes(filter)) return true;
    if (hasMatchingDescendant(node.children, filter)) return true;
  }
  return false;
}

function findNodeName(nodes: BpaNode[], id: string): string | null {
  for (const node of nodes) {
    if (node.id === id) return node.name;
    const found = findNodeName(node.children, id);
    if (found) return found;
  }
  return null;
}

type LoadState = "idle" | "loading" | "loaded" | "failed";

function idleCopy(load: LoadState, treeSize: number): { message: string; hint: string } {
  if (load === "failed") return { message: "No practice areas to show", hint: "" };
  if (load === "loaded" && treeSize === 0) {
    return { message: "No practice areas in the taxonomy", hint: "" };
  }
  return {
    message: "Browse the taxonomy tree",
    hint: "Expand groups or search to find a practice area",
  };
}

export function TaxonomyBpaTierSelector({
  value,
  onChange,
  disabled = false,
  variant = "search",
}: TaxonomyBpaTierSelectorProps) {
  const [open, setOpen] = useState(false);
  const [tree, setTree] = useState<BpaNode[]>([]);
  const [load, setLoad] = useState<LoadState>("idle");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [resolvedLabel, setResolvedLabel] = useState<string | null>(null);
  const filter = search.toLowerCase().trim();

  const selectedLabel = value ? (findNodeName(tree, value) ?? resolvedLabel) : null;

  // Fetch the display name for the initial value before the tree is loaded
  useEffect(() => {
    if (!value || tree.length > 0) {
      setResolvedLabel(null);
      return;
    }
    let cancelled = false;
    taxonomyBpas.getById(value).then(
      bpa => {
        if (!cancelled) setResolvedLabel(bpa.name);
      },
      () => {
        /* ignore - the placeholder is an acceptable fallback */
      },
    );
    return () => {
      cancelled = true;
    };
  }, [value, tree.length]);

  useEffect(() => {
    if (!open || load !== "idle") return;
    setLoad("loading");
    taxonomyBpas
      .getAll()
      .then(all => {
        setTree(buildTree(all));
        setLoad("loaded");
      })
      .catch(() => setLoad("failed"));
  }, [open, load]);

  function handleOpen() {
    if (disabled) return;
    if (load === "failed") setLoad("idle");
    setOpen(true);
    setSearch("");
  }

  function toggleExpand(id: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSelect(id: string) {
    onChange(id);
    setOpen(false);
  }

  const visible = flattenVisible(tree, expanded, filter, 0);

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className={styles.trigger}
        data-variant={variant}
        onClick={handleOpen}
        disabled={disabled}
      >
        {selectedLabel ? (
          <span className={styles.triggerValue}>{selectedLabel}</span>
        ) : (
          <span className={styles.triggerPlaceholder}>Select taxonomy BPA...</span>
        )}
        {variant === "dropdown" ? (
          <span className={styles.triggerSearchIcon} aria-hidden="true">
            <ChevronIcon />
          </span>
        ) : (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={styles.triggerSearchIcon}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        )}
      </button>
      {value && !disabled && (
        <button
          type="button"
          className={styles.clearButtonInline}
          onClick={() => onChange(null)}
          aria-label="Clear taxonomy BPA"
        >
          ×
        </button>
      )}

      <SearchModal
        open={open}
        onClose={() => setOpen(false)}
        title="Select taxonomy BPA"
        query={search}
        onQueryChange={setSearch}
        placeholder="Search practice areas..."
        searching={load === "loading"}
        hasResults={visible.length > 0}
        resultCount={visible.length}
        idleMessage={idleCopy(load, tree.length).message}
        idleHint={idleCopy(load, tree.length).hint}
        error={
          load === "failed"
            ? "The taxonomy couldn't be loaded. Close and reopen to try again."
            : null
        }
        emptyMessage={filter ? `No results for "${search}"` : "No practice areas"}
        hideIdleFooter={load === "failed" || (load === "loaded" && tree.length === 0)}
      >
        <ul className={styles.list}>
          {visible.map(({ node, depth }) => {
            const hasChildren = node.children.length > 0;
            const isExpanded = expanded.has(node.id) || Boolean(filter);
            const isSelected = value === node.id;
            return (
              <li
                key={node.id}
                className={`${styles.item} ${isSelected ? styles.itemSelected : ""}`}
                style={{ paddingLeft: `${12 + depth * 20}px` }}
              >
                {hasChildren ? (
                  <button
                    type="button"
                    className={styles.expandToggle}
                    onClick={() => toggleExpand(node.id)}
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                  >
                    <span
                      className={styles.chevron}
                      style={{ transform: isExpanded ? undefined : "rotate(-90deg)" }}
                    >
                      <ChevronIcon />
                    </span>
                  </button>
                ) : (
                  <span className={styles.expandSpacer} />
                )}
                <button
                  type="button"
                  className={styles.itemButton}
                  onClick={() => handleSelect(node.id)}
                >
                  {node.name}
                  {hasChildren && <span className={styles.childCount}>{node.children.length}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </SearchModal>
    </div>
  );
}
